import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import {
  selectIsInboxBootstrapped,
  isAuthenticatedSelector,
  fetchingStatusesSelector,
  selectCurrentOpenNavSections,
  selectCurrentMailbox as cachedMailboxObject,
} from 'selectors/app/base'
import { currentPathSelector } from 'selectors/app/rest'
import {
  selectCurrentUserIsAdminOrOwner,
  selectPrefersAgentsCanMoveToInaccessibleMailboxes,
  selectPrefersUnifiedInbox,
} from 'ducks/currentUser/selectors'
import { selectIsInInbox } from 'selectors/location'

import {
  _selectSortedIds,
  _selectCommonWords,
  selectMailboxesById,
  decorate,
  selectKnownMailboxes,
  selectMailboxesByName,
} from 'selectors/mailboxes_base'
import { selectRawById, selectCurrentTicketMailboxId } from 'selectors/tickets'
import {
  selectSelectedTicketIds,
  selectIsTicketSelectionMode,
} from 'selectors/ticket_list/base'

import {
  compact,
  emptyArr,
  isEmpty,
  sortByKeyInsensitive,
  uniq,
} from 'util/arrays'
import { toDate } from 'util/date'
import { createBasicSelector } from 'util/redux'

// The currentMailbox in the store can have either an id (from folder paths) or a
// name (from search paths).
export const selectCurrentMailboxId = createSelector(
  cachedMailboxObject,
  selectMailboxesByName,
  selectMailboxesById,
  (mailbox, byName, byId) => {
    if (!mailbox) return null
    if (mailbox.id) return mailbox.id
    const idOrName = mailbox.name
    if (idOrName && byName[idOrName]) return byName[idOrName].id
    if (idOrName && byId[idOrName]) return byId[idOrName].id
    return null
  }
)

export const selectMailboxIdFromTicketOrCurrent = createBasicSelector(
  selectCurrentMailboxId,
  selectCurrentTicketMailboxId,
  (currentMailboxId, currentTicketMailboxId) => {
    return currentMailboxId || currentTicketMailboxId || null
  }
)

export const selectCurrentMailbox = createSelector(
  selectMailboxesById,
  selectCurrentMailboxId,
  _selectSortedIds,
  currentPathSelector,
  (byId, mailboxId, sortedIds, path) => {
    if (!mailboxId && path && path.match(/^\/folders/)) {
      return null
    }
    if (!mailboxId) return null
    const found = byId[mailboxId]
    if (found) {
      return found
    }
    return null
  }
)

export const selectCurrentMailboxName = createSelector(
  selectCurrentMailbox,
  mailbox => {
    if (!mailbox) return null
    return mailbox.name
  }
)

export const selectIsCurrentDemoMailbox = createSelector(
  selectCurrentMailbox,
  mailbox => {
    if (!mailbox) return null
    return mailbox.state === 'demo'
  }
)

export const selectShouldFetchCurrentMailbox = createSelector(
  isAuthenticatedSelector,
  selectCurrentMailbox,
  fetchingStatusesSelector,
  (auth, mailbox, statuses) => {
    if (!auth) return false
    // we don't have mailboxes and we're not currently fetching them
    return !mailbox && !statuses.fetchCurrentMailboxWithTickets
  }
)

const selectInaccessibleMailboxesRaw = state =>
  state.mailboxes.inaccessibleMailboxes

export const selectIsMailboxInaccessible = (state, mailboxId) => {
  if (!mailboxId) return false
  const inaccessibleMailboxes = selectInaccessibleMailboxesRaw(state)
  const inaccessibleMailboxIds = inaccessibleMailboxes.map(m => m.id)
  return inaccessibleMailboxIds.indexOf(mailboxId) !== -1
}

export const selectInaccessibleMailbox = (state, mailboxId) => {
  if (!mailboxId) return null
  const inaccessibleMailboxes = selectInaccessibleMailboxesRaw(state)
  return inaccessibleMailboxes.find(m => m.id === mailboxId)
}

export const selectInaccessibleMailboxesIds = createSelector(
  selectInaccessibleMailboxesRaw,
  inaccessibleMailboxesRaw => {
    return (inaccessibleMailboxesRaw || []).map(m => m.id) || emptyArr
  }
)

export const selectInaccessibleMailboxes = createSelector(
  selectInaccessibleMailboxesRaw,
  _selectCommonWords,
  (inaccessibleMailboxes, commonWords) => {
    if (inaccessibleMailboxes) {
      return inaccessibleMailboxes.map(mailbox =>
        decorate(commonWords)(mailbox)
      )
    }
    return null
  }
)

const selectSelectedTicketsMailboxIds = createSelector(
  selectSelectedTicketIds,
  selectRawById,
  (ids, byId) => {
    if (!ids || isEmpty(ids)) return emptyArr
    return uniq(
      compact(
        ids.filter(id => {
          return byId[id] ? byId[id].mailboxId : undefined
        })
      )
    )
  }
)

export const selectSelectedTicketsMailboxes = createSelector(
  selectSelectedTicketsMailboxIds,
  selectMailboxesById,
  (mailboxIds, mailboxesById) => {
    if (isEmpty(mailboxIds)) return emptyArr
    return compact(mailboxIds.map(id => mailboxesById[id]))
  }
)

export const selectPossibleMailboxesForChange = createSelector(
  selectCurrentTicketMailboxId,
  selectKnownMailboxes,
  selectSelectedTicketsMailboxes,
  selectInaccessibleMailboxes,
  selectPrefersAgentsCanMoveToInaccessibleMailboxes,
  selectIsTicketSelectionMode,
  (
    currentMailboxId,
    knownMailboxes,
    selectedTickets,
    inaccessibleMailboxes,
    canMoveToInaccessible,
    isBulkMode
  ) => {
    let possibleMailboxes
    if (isBulkMode) {
      possibleMailboxes = knownMailboxes
    } else {
      let mailboxId = currentMailboxId
      if (!mailboxId && selectedTickets && selectedTickets.length === 1) {
        mailboxId = selectedTickets[0].id
      }
      possibleMailboxes = knownMailboxes.filter(
        mailbox => mailbox.id !== mailboxId
      ) // exclude draft mailbox
    }
    const joined =
      canMoveToInaccessible && inaccessibleMailboxes
        ? possibleMailboxes.concat(inaccessibleMailboxes)
        : possibleMailboxes
    const mailboxes = sortByKeyInsensitive(joined, 'label')
    return mailboxes.length > 0 ? mailboxes : emptyArr
  }
)

export const selectOtherMailbox = createSelector(
  selectPossibleMailboxesForChange,
  mailboxes => (mailboxes.length === 1 ? mailboxes[0] : undefined)
)

const navSectionSearches = {
  open: { state: 'opened' },
  closed: { state: 'closed' },
  snoozed: { state: 'snoozed' },
}

// Helper function that we can call from outside the Left Nav context to build
// the navigation search objects.
export function buildNavSearch(mailboxId, navSection) {
  return {
    mailbox: mailboxId,
    ...navSectionSearches[navSection],
  }
}

export const selectCurrentNavigationSearches = createSelector(
  selectCurrentMailboxId,
  selectCurrentOpenNavSections,
  (mailboxId, navSections) => {
    return navSections
      .map(navSection => buildNavSearch(mailboxId, navSection))
      .concat({ mailbox: mailboxId })
  }
)

export const selectShowDemoMailboxBar = createSelector(
  selectIsInboxBootstrapped,
  selectKnownMailboxes,
  selectIsInInbox,
  // eslint-disable-next-line no-unused-vars
  (bootstrapped, mailboxes, isInInbox) => {
    // TODO: temp: Nick wants this hidden for now
    return false
    /* if (!bootstrapped || !mailboxes || !isInInbox) {
      return false
    }

    // If the agent has no mailbox at all we don't want to show the demo bar
    if (isEmpty(mailboxes)) return false

    const hasRealMailbox = mailboxes.some(mailbox => {
      return mailbox.state !== 'demo'
    })
    return !hasRealMailbox */
  }
)

const mailboxesByStatesCacheKey = (
  _state,
  mailboxStates,
  _createdAfter,
  channelTypes
) =>
  [
    (mailboxStates || ['empty']).join(','),
    (channelTypes || ['all']).join(','),
  ].join('|')

export const selectMailboxByStates = createCachedSelector(
  selectIsInboxBootstrapped,
  selectKnownMailboxes,
  (_state, mailboxStates) => mailboxStates,
  (_state, _mailboxStates, createdAfter) => createdAfter,
  (_state, _mailboxStates, _createdAfter, channelTypes) => channelTypes,
  (
    isInboxBootstrapped,
    mailboxes,
    mailboxStates,
    createdAfter,
    channelTypes
  ) => {
    if (!isInboxBootstrapped || !mailboxes || !mailboxStates) {
      return []
    }

    if (isEmpty(mailboxes)) return []

    return mailboxes.filter(m => {
      if (createdAfter && toDate(m.created_at) < createdAfter) {
        return false
      }

      if (channelTypes && !channelTypes.includes(m.channel_type)) {
        return false
      }

      return mailboxStates.includes(m.state)
    })
  }
)(mailboxesByStatesCacheKey)

export const selectHasAnyMailboxByStates = createCachedSelector(
  selectMailboxByStates,
  mailboxByStates => {
    return mailboxByStates.length > 0
  }
)(mailboxesByStatesCacheKey)

export const selectDoesCurrentMailboxHaveState = createCachedSelector(
  selectCurrentMailbox,
  selectIsInboxBootstrapped,
  selectHasAnyMailboxByStates,
  selectIsInInbox,
  selectPrefersUnifiedInbox,
  (_state, mailboxStates) => mailboxStates,
  (_state, _mailboxStates, createdAfter) => createdAfter,
  (_state, _mailboxStates, _createdAfter, channelTypes) => channelTypes,
  (
    currentMailbox,
    isInboxBootstrapped,
    hasAnyMailboxByStates,
    isInInbox,
    prefersUnifiedInbox,
    mailboxStates,
    createdAfter,
    channelTypes
  ) => {
    if (!isInInbox || !isInboxBootstrapped || !mailboxStates) {
      return false
    }

    if (!currentMailbox) {
      if (prefersUnifiedInbox) {
        // most likely on Unified inbox, search all mailboxes for state
        return hasAnyMailboxByStates
      }
      // On all inboxes, don't seach all mailboxes for state
      return false
    }

    if (createdAfter && toDate(currentMailbox.created_at) < createdAfter) {
      return false
    }

    return (
      mailboxStates.includes(currentMailbox.state) &&
      (!channelTypes || channelTypes.includes(currentMailbox.channel_type))
    )
  }
)(mailboxesByStatesCacheKey)

/**
 * only includes inaccessible if the user is an owner/admin
 */
export const selectMailboxesIncludingInaccessible = createSelector(
  selectCurrentUserIsAdminOrOwner,
  selectKnownMailboxes,
  selectInaccessibleMailboxes,
  (currentUserIsAdminOrOwner, knownMailboxes, inaccessibleMailboxes) => {
    if (!currentUserIsAdminOrOwner || !inaccessibleMailboxes) {
      return knownMailboxes
    }

    return [...knownMailboxes, ...inaccessibleMailboxes]
  }
)
